<script lang="ts">
  // BJL-CHECK: Alert (used, the get-off-here strip), Button (used, dismiss). The Board CTA
  // is the one control that stays custom: it is painted in the leg's MTA route color, which
  // no bjl variant can express. See artifacts/deviations/RiderPanel.md.
  //
  // Plan prompt: awaiting_board / transfer. No vehicle is bound yet. Tell the rider which
  // line to get on, and — when exactly one train of that line is close enough to be
  // unambiguous — let them board without hunting for it on the map.
  import { Alert } from '@byjackli-labs/ui/Alert'
  import { Button } from '@byjackli-labs/ui/Button'
  import { Colors, getRouteColor, getRouteTextColor, sameRouteFamily } from '../../ui/colors'
  import { Type } from '../../ui/theme'
  import { useRiderStore } from '../../store/rider'
  import { useTransitStore } from '../../store/transit'
  import { useGeolocation } from '../../hooks/useGeolocation.svelte'
  import { nearestVehicles } from '../../lib/nearest'
  import { storeSelect, storeState } from '../../lib/store.svelte'
  import { panelShell, BOARD_RADIUS_M } from './shell'
  import RouteBadge from '../RouteBadge.svelte'

  // Whole-store read, matching the React `useRiderStore()`: `currentLeg`/`transferWalk` are
  // getters over the plan, so any rider-store change can change what they return. The store
  // is cold (ride events and a 30 s stop refresh), unlike the vehicle stream below.
  const rider = storeState(useRiderStore)
  const vehicles = storeSelect(useTransitStore, (s) => s.vehicles)

  const geo = useGeolocation()

  const leg = $derived(rider.value.currentLeg())
  const walk = $derived(rider.value.transferWalk())
  const isTransfer = $derived(rider.value.phase === 'transfer')

  const nearby = $derived(
    nearestVehicles(vehicles.value, geo.location, {
      maxDistanceMeters: BOARD_RADIUS_M,
      limit: 8,
    }),
  )
  const candidates = $derived(
    leg ? nearby.filter((n) => sameRouteFamily(leg.routeId, n.vehicle.routeId)) : [],
  )
  // Two trains of the same line within 400 m means we can't tell which one the rider is
  // on — make them tap the right one rather than guess wrong.
  const boardable = $derived(candidates.length === 1 ? candidates[0].vehicle : null)
</script>

{#if leg}
  <!-- Same BOTTOM drawer as the riding face (see `panelShell`), so the prompt and the sign it
       turns into occupy the same edge — boarding changes what the drawer says, not where it
       is. No safe-area padding: the tab bar it stands on has already paid the nav inset. -->
  <div style="{panelShell} padding: 16px;">
    {#if isTransfer && walk}
      <div style="margin-bottom: 12px;">
        <Alert variant="warning" size="sm">
          🚶 Get off here — {walk.minutes} min walk to {walk.toName}
        </Alert>
      </div>
    {/if}

    <div style="display: flex; align-items: center; gap: 12px;">
      <RouteBadge routeId={leg.routeId} size={34} />
      <div style="flex: 1; min-width: 0;">
        <div style="color: {Colors.text}; {Type.title};">
          {isTransfer ? 'Then take the' : 'Board the'}
          {leg.routeId}{leg.headsign ? ` toward ${leg.headsign}` : ''}
        </div>
        <div
          style="
            color: {Colors.textSecondary}; {Type.body}; margin-top: 2px;
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          "
        >
          From {leg.from.name} → off at {leg.to.name}
        </div>
      </div>
      <Button
        variant="text"
        size="sm"
        iconOnly
        onclick={() => useRiderStore.getState().endRide()}
        aria-label="End trip"
      >✕</Button>
    </div>

    <button
      onclick={() => boardable && useRiderStore.getState().startRide(boardable.vehicleId, boardable)}
      disabled={!boardable}
      style="
        width: 100%; margin-top: 14px; padding: 13px 0;
        border: none; border-radius: var(--bjl-radius-md, 12px);
        cursor: {boardable ? 'pointer' : 'default'};
        background-color: {boardable ? getRouteColor(leg.routeId) : Colors.surfaceElevated};
        color: {boardable ? getRouteTextColor(leg.routeId) : Colors.textMuted};
        font-weight: 800; font-size: var(--bjl-font-size-md);
      "
    >
      {boardable
        ? `${leg.mode === 'bus' ? '🚌' : '🚇'} Board this ${leg.routeId}`
        : 'Tap your vehicle on the map to board'}
    </button>
  </div>
{/if}
